import customNanoId from 'lib/nanoid/nanoid';
import {
  MultipleChoiceCropData,
  MultipleChoiceImage,
  MultipleChoiceOption,
  MultipleChoiceProperties,
} from '../multiple-choice.constants';
import { FILL, ImageCropData, Mode } from '../../image/image-crop.constants';
import { Image } from '../../shared/component-image-modal/component-image-modal.constants';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';

export function getOptionsPayload(options: MultipleChoiceOption[]) {
  // null id and sort should never happen but may be the
  // case for stale data when we just release this change
  // to add an id and sort value
  return options.map(option => {
    const { __typename, cropData, imageUrls, ...rest } = option;

    return {
      ...rest,
      itemId: option.itemId || customNanoId(),
      sort: option.sort || 'v',
      cropData: cropData
        ? {
            x: cropData.x,
            y: cropData.y,
            width: cropData.width,
            height: cropData.height,
            zoom: cropData.zoom,
            naturalWidth: cropData.naturalHeight,
            naturalHeight: cropData.naturalHeight,
          }
        : null,
      image: option.image
        ? {
            itemId: option.image.itemId,
            fileName: option.image.fileName,
            key: option.image.key,
            alt: option.image.alt,
            data: option.image.data
              ? removeTypenameFromObject(option.image.data)
              : null,
          }
        : null,
      imageLayout: option.imageLayout || FILL,
    };
  });
}

export function getRowAndGridOptions(numberOfOptions: number) {
  if (numberOfOptions === 3) {
    return ['1x3', '3x1'];
  }
  if (numberOfOptions === 4) {
    return ['1x4', '2x2', '4x1'];
  }
  if (numberOfOptions === 5) {
    return ['1x5', '2x3', '3x2', '5x1'];
  }
  if (numberOfOptions === 6) {
    return ['1x6', '2x3', '3x2', '6x1'];
  }
  if (numberOfOptions === 7) {
    return ['1x7', '2x4', '3x3', '4x2', '7x1'];
  }
  if (numberOfOptions === 8) {
    return ['1x8', '2x4', '4x2', '8x1'];
  }
  if (numberOfOptions === 9) {
    return ['1x9', '2x5', '5x2', '3x3', '9x1'];
  }
  if (numberOfOptions === 10) {
    return ['1x10', '2x5', '5x2', '10x1'];
  }
  if (numberOfOptions === 11) {
    return ['1x11', '2x6', '3x4', '4x3', '6x2', '11x1'];
  }
  if (numberOfOptions === 12) {
    return ['1x12', '2x6', '3x4', '4x3', '6x2', '12x1'];
  }
  return ['1x2', '2x1'];
}

export function updateOptionText(
  options: MultipleChoiceOption[],
  itemId: string,
  text: string | null
) {
  return options.map((option, i) => {
    return {
      ...option,
      text: option.itemId === itemId ? text : option.text,
    };
  });
}

export function updateOptionImageData({
  options,
  id,
  cropData,
  altText,
  imageLayout,
  image,
  sourceUrl,
}: {
  options: MultipleChoiceOption[];
  id: string | null;
  altText: string | null;
  cropData: ImageCropData | null;
  imageLayout: Mode;
  image: Image | null;
  sourceUrl: string | null;
}) {
  return options.map(option =>
    option.itemId === id
      ? {
          ...option,
          imageLayout,
          sourceUrl,
          cropData: cropData
            ? ({
                ...cropData,
                __typename: 'CropData',
              } as MultipleChoiceCropData)
            : null,
          image: image
            ? ({
                __typename: 'MultiChoiceOptionImage',
                itemId: option.image?.itemId || id,
                key: image.key,
                fileName: image.fileName,
                original: image.original,
                alt: altText,
                data: image.data,
              } as MultipleChoiceImage)
            : null,
        }
      : option
  );
}

export function getMultipleChoiceImageContainerDimensions(
  id: string | undefined | null
) {
  const el = document.getElementById(`multi-choice-${id}`);
  return {
    width: el?.offsetWidth || 300,
    height: el?.offsetHeight || 300,
  };
}

export function generateTextFieldKey(
  fontSize: number,
  properties: MultipleChoiceProperties
) {
  return [
    fontSize,
    properties.layout?.display,
    properties.multipleChoice.rowsGridLayout,
    properties.multipleChoice.showOptionLetter,
    properties.multipleChoice.imagePosition,
  ].join('');
}
